import type { MouseEventHandler, PropsWithChildren } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./FilterDialogContainer.module.scss";

export const FilterDialogSection = ({
  title,
  content,
  subtitle,
  className,
}: {
  title: string | JSX.Element;
  content: JSX.Element;
  subtitle?: string;
} & PlatformComponentProps) => {
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.FilterDialogContainer
  );
  return (
    <div className={clsx(cn("section"), className)}>
      <div className={cn("section-title")}>
        <div className={cn("title-text-wrapper")}>
          {typeof title === "string" ? (
            <h2 className={cn("title")}>{title}</h2>
          ) : (
            title
          )}
          {subtitle !== undefined ? (
            <h2 className={cn("subtitle")}>{subtitle}</h2>
          ) : null}
        </div>
      </div>
      <div className={cn("section-content")}>{content}</div>
    </div>
  );
};

export interface FilterDialogComponentProps {
  className?: string;
  onClickDialogTrigger?: MouseEventHandler<HTMLButtonElement>;
  onClickReset: MouseEventHandler<HTMLButtonElement>;
  onClickApply: MouseEventHandler<HTMLButtonElement>;
  numberOfFilters?: number;
  buttonText?: string;
  filtersChanged?: boolean;
  dialogTitle?: string;
  applyText?: string;
}
export const FilterDialogContainer = ({
  children,
  className,
  onClickDialogTrigger,
  onClickReset,
  onClickApply,
  numberOfFilters,
  buttonText,
  filtersChanged,
  dialogTitle,
  applyText,
}: PropsWithChildren<FilterDialogComponentProps>) => {
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.FilterDialogContainer
  );

  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const defaultDialogTitle = isDesktopAndUp
    ? t("core-ui.filters.dialog.openerLabel")
    : t("core-ui.filters.dialog.openerLabelMobile");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className={clsx(cn("button"), className)}
          onClick={(e) => onClickDialogTrigger?.(e)}
        >
          <span className={cn("button-text")}>
            {isDesktopAndUp
              ? t("searchFilter.allFilters")
              : (buttonText ?? t("core-cars.availabilityPage.filters.button"))}

            {numberOfFilters != null && numberOfFilters > 0 ? (
              <>
                <span className={cn("filter-number", ["mobile"])}>
                  {numberOfFilters}
                </span>
                <span
                  className={cn("filter-number", ["desktop"])}
                >{`(${numberOfFilters})`}</span>
              </>
            ) : null}
          </span>

          <Icon useCurrentColor iconName={IconNameEnum.filter} />
        </button>
      </DialogTrigger>
      <DialogContent
        title={dialogTitle ?? defaultDialogTitle}
        className={clsx(block, className)}
        bottomFixedContent={
          <div className={cn("bottom-section")}>
            {(filtersChanged != null && filtersChanged) ||
            (numberOfFilters != null && numberOfFilters > 0) ? (
              <DialogClose asChild>
                <Button
                  onClick={onClickReset}
                  className={cn("reset-button")}
                  buttonType={ButtonTypeEnum.Secondary}
                >
                  {t("core-ui.filters.dialog.clear")}
                </Button>
              </DialogClose>
            ) : null}
            <DialogClose asChild>
              <Button
                className={cn("apply-button")}
                onClick={onClickApply}
                disabled={
                  numberOfFilters != null &&
                  numberOfFilters < 1 &&
                  filtersChanged != null &&
                  !filtersChanged
                }
              >
                {applyText ?? t("core-ui.filters.dialog.apply")}
              </Button>
            </DialogClose>
          </div>
        }
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
